import { basename } from "@tauri-apps/api/path";

import {
	$addAttachmentFromBytes,
	$documentStoreV2,
	QUERYABLE_EXTENSIONS,
	isQueryableFile,
	readBinaryFile,
	store,
} from "@moment/state";
import { makeToast } from "@moment/ui";

import { expandAttachmentPane } from "../navigation-pane/expandAttachmentPane";

export function parseAllFileUris(uriList: string): string[] {
	const lines = uriList.split(/\r?\n/).filter((line) => line && !line.startsWith("#"));
	const paths: string[] = [];

	for (const uri of lines) {
		if (uri.startsWith("file://")) {
			try {
				paths.push(decodeURIComponent(uri.replace(/^file:\/\//, "")));
			} catch {
				console.warn("[attachments] Failed to decode file URI:", uri);
			}
		}
	}

	return paths;
}

export function extractAllFileUrlsFromHtml(htmlData: string): string[] {
	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlData, "text/html");
	const paths: string[] = [];
	const seen = new Set<string>();

	const addPath = (raw: string) => {
		try {
			const p = decodeURIComponent(raw.replace(/^file:\/\//, ""));
			if (!seen.has(p)) {
				seen.add(p);
				paths.push(p);
			}
		} catch {
			console.warn("[attachments] Failed to decode file URL:", raw);
		}
	};

	const anchors = doc.querySelectorAll("a");
	for (const anchor of anchors) {
		const textContent = anchor.textContent?.trim();
		if (textContent?.startsWith("file://")) {
			addPath(textContent);
		}
	}

	for (const anchor of anchors) {
		const href = anchor.getAttribute("href");
		if (href?.startsWith("file://")) {
			addPath(href);
		}
	}

	const imgs = doc.querySelectorAll("img");
	for (const img of imgs) {
		const src = img.getAttribute("src");
		if (src?.startsWith("file://")) {
			addPath(src);
		}
	}

	return paths;
}

async function readAnyFileFromPath(
	filePath: string
): Promise<{ name: string; contents: ArrayBuffer } | undefined> {
	const contents = await readBinaryFile(filePath);
	if (!contents) {
		console.error("[attachments] Failed to read file:", filePath);
		return undefined;
	}

	const filename = await basename(filePath);

	return {
		name: filename,
		contents: contents.buffer as ArrayBuffer,
	};
}

export interface ReadFilesResult {
	files: { name: string; contents: ArrayBuffer }[];
	failedCount: number;
}

async function readAllFilesFromPaths(filePaths: string[]): Promise<ReadFilesResult> {
	const results = await Promise.allSettled(filePaths.map((p) => readAnyFileFromPath(p)));
	let failedCount = 0;
	const files: { name: string; contents: ArrayBuffer }[] = [];
	for (const r of results) {
		if (r.status === "fulfilled" && r.value) {
			files.push(r.value);
		} else {
			failedCount++;
		}
	}
	return { files, failedCount };
}

export async function readAllFilesFromUri(uriList: string): Promise<ReadFilesResult> {
	return readAllFilesFromPaths(parseAllFileUris(uriList));
}

export async function readAllFilesFromHtml(htmlData: string): Promise<ReadFilesResult> {
	return readAllFilesFromPaths(extractAllFileUrlsFromHtml(htmlData));
}

export async function fileToAttachmentData(
	file: File
): Promise<{ name: string; contents: ArrayBuffer }> {
	return {
		name: file.name,
		contents: await file.arrayBuffer(),
	};
}

export async function persistAttachment(
	documentId: string,
	name: string | undefined,
	contents: ArrayBuffer | undefined
) {
	if (!name || !contents) {
		return;
	}

	const storeV2 = (await store.get($documentStoreV2(documentId))) ?? undefined;

	await store.set($addAttachmentFromBytes, {
		documentId,
		filename: name,
		contents: new Uint8Array(contents),
		store: storeV2,
	});
}

/**
 * Persist already-read attachment data sequentially, then show batched
 * success/error toasts and expand the attachment pane on success.
 * Non-queryable files are rejected with a specific toast.
 */
export async function persistAttachmentsWithReport(
	documentId: string,
	data: { name: string; contents: ArrayBuffer }[],
	failedReadCount = 0
): Promise<void> {
	const supported = data.filter((d) => isQueryableFile(d.name));
	const unsupportedCount = data.length - supported.length;

	let succeeded = 0;
	for (const d of supported) {
		try {
			await persistAttachment(documentId, d.name, d.contents);
			succeeded++;
		} catch (error) {
			console.error("[attachments] Failed to persist file", error);
		}
	}
	const totalFailed = failedReadCount + (supported.length - succeeded);
	if (succeeded > 0) {
		makeToast({ variant: "success", message: `Attached ${succeeded} file(s)` });
		expandAttachmentPane(documentId);
	}
	if (totalFailed > 0) {
		makeToast({
			variant: "error",
			message: `Failed to attach ${totalFailed} file(s)`,
			timer: "dismissible",
		});
	}
	if (unsupportedCount > 0) {
		makeToast({
			variant: "error",
			timer: "dismissible",
			message: `${unsupportedCount} file(s) skipped — only ${[...QUERYABLE_EXTENSIONS].join(", ")} files are supported`,
		});
	}
}

/**
 * Convert browser File objects to attachment data, persist sequentially,
 * then show batched success/error toasts.
 * Non-queryable files are rejected before persistence.
 */
export async function persistFileAttachmentsWithReport(
	documentId: string,
	files: File[]
): Promise<void> {
	const supportedFiles = files.filter((f) => isQueryableFile(f.name));
	const unsupportedCount = files.length - supportedFiles.length;

	const converted: { name: string; contents: ArrayBuffer }[] = [];
	let convertFailed = 0;
	for (const file of supportedFiles) {
		try {
			converted.push(await fileToAttachmentData(file));
		} catch (error) {
			console.error("[attachments] Failed to read file data", error);
			convertFailed++;
		}
	}
	if (unsupportedCount > 0 && converted.length === 0) {
		makeToast({
			variant: "error",
			timer: "dismissible",
			message: `Only ${[...QUERYABLE_EXTENSIONS].join(", ")} files are supported`,
		});
		return;
	}
	if (unsupportedCount > 0) {
		makeToast({
			variant: "error",
			timer: "dismissible",
			message: `${unsupportedCount} file(s) skipped — only ${[...QUERYABLE_EXTENSIONS].join(", ")} files are supported`,
		});
	}
	await persistAttachmentsWithReport(documentId, converted, convertFailed);
}
